import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute } from "@angular/router";
import { API_LEVERAGED_TOKEN_TYPE, SECURITY_ASSET_GROUP_LIST } from "@helpers/constants";
import { Fiat } from "@models";
import { Index } from "@models/asset/index/index.model";
import { Tradable } from "@models/interfaces/tradable";
import { BpOption } from "@models/ui-components/bp-option";
import { AccountService } from "@modules/account/services/account.service";
import { BuyAssetListFacade, BuyListModel } from "@modules/trade/components/buy-asset-list/buy-asset-list.facade";
import { ASSET_LIST_FILTERS, ASSET_LIST_TIMEFRAMES } from "@modules/trade/constants/constants";
import { getTradableChange } from "@modules/trade/helpers/helper";
import { TradeAgainService } from "@modules/trade/services/trade-again.service";
import { combineLatest, Observable, take } from "rxjs";
import { SegmentService } from "@main-services/segment.service";
import { MtTypeSwitcherOption } from "@modules/margin-trading/components/mt-type-switcher/mt-type-switcher.component";
import { RudderstackService } from "@main-services/rudderstack.service";
import { config } from "@config";
import { RealStocksFacade } from "@modules/real-stocks-temp/real-stocks.facade";
import { DYNAMIC_CONFIGS, RS_REDIRECTION_TYPE, StatsigClientService } from "@core-services/statsig/statsig.service";

interface BuyListModelComponent {
	vm: BuyListModel;
	buyAgainList: Tradable[];
}

@Component({
	selector: "buy-asset-list",
	templateUrl: "./buy-asset-list.component.html",
	styleUrls: ["./buy-asset-list.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [TradeAgainService, BuyAssetListFacade],
	standalone: false
})
export class BuyAssetListComponent implements OnInit {
	@Input() public isPriceOrder: boolean = false;
	@Output() public assetClicked: EventEmitter<{ tradable: Tradable; isMarginAsset: boolean }> = new EventEmitter<{
		tradable: Tradable;
		isMarginAsset: boolean;
	}>();

	public readonly viewModel$: Observable<BuyListModelComponent> = combineLatest({
		vm: this.buyAssetListFacade.viewModel$(),
		buyAgainList: this.tradeAgainService.getBuyAgainList$()
	});

	private realSecuritiesRedirectionType$ = this.statsigService.getDynamicConfig<Record<"value", RS_REDIRECTION_TYPE>>(
		DYNAMIC_CONFIGS.RS_REDIRECTION_TYPE
	);

	public REDIRECTION_TYPE = RS_REDIRECTION_TYPE;
	public readonly defaultFiat: Fiat = this.accountService.getDefaultFiat();
	public scrollToTop: boolean = false;

	pillsThatNeedRealSecuritiesBanner = SECURITY_ASSET_GROUP_LIST;

	constructor(
		private readonly accountService: AccountService,
		private readonly tradeAgainService: TradeAgainService,
		private readonly buyAssetListFacade: BuyAssetListFacade,
		private readonly activatedRoute: ActivatedRoute,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly realStocksFacade: RealStocksFacade,
		private readonly statsigService: StatsigClientService
	) {}

	ngOnInit(): void {
		this.activatedRoute.queryParams.pipe(take(1)).subscribe(({ selectedPill }) => {
			if (selectedPill && selectedPill === API_LEVERAGED_TOKEN_TYPE) {
				this.onFilterChange(ASSET_LIST_FILTERS.find(({ value }) => value === API_LEVERAGED_TOKEN_TYPE));
			}
		});
	}

	public onScrolledToBottom(isMarginSelected: boolean, hasNextPage: boolean, after: string): void {
		this.buyAssetListFacade.setPageSize(isMarginSelected, hasNextPage, after);
	}

	public onClick(tradable: Tradable, isMarginAsset: boolean): void {
		this.assetClicked.emit({ tradable, isMarginAsset });
	}

	public onFilterChange(option: BpOption): void {
		this.scrollToTop = true;
		this.buyAssetListFacade.setPill(option);
		if (option.value === API_LEVERAGED_TOKEN_TYPE) {
			this.buyAssetListFacade
				.getMarginAssets$()
				.pipe(take(1))
				.subscribe((res) => this.buyAssetListFacade.marginAssets$$.next(res));
		}
	}

	public onSortChange(sortState: Sort): void {
		this.buyAssetListFacade.setSorting(sortState);
		this.scrollToTop = true;
	}

	public onSearchValueChange(val: string): void {
		this.buyAssetListFacade.setSearching(val);
	}

	public onLeverageTypeChange(type: MtTypeSwitcherOption): void {
		this.buyAssetListFacade.setLeverageType(type);
		this.segmentService.track({
			name: "Discover Leverage Selection Option Clicked",
			properties: {
				option: type === MtTypeSwitcherOption.LEVERAGE ? "leveraged_tokens" : "margin_trading",
				screen_it_happened_on: "buy_flow"
			}
		});
		this.rudderstackService.track({
			name: "Discover Leverage Selection Option Clicked",
			properties: {
				option: type === MtTypeSwitcherOption.LEVERAGE ? "leveraged_tokens" : "margin_trading",
				screen_it_happened_on: "buy_flow"
			}
		});
	}

	trackLeverageSelectionClicked(): void {
		this.segmentService.track({
			name: "Discover Leverage Selection Clicked",
			properties: {
				screen_it_happened_on: "buy_flow"
			}
		});
		this.rudderstackService.track({
			name: "Discover Leverage Selection Clicked",
			properties: {
				screen_it_happened_on: "buy_flow"
			}
		});
	}

	public getChangePercent(tradable: Tradable): number {
		return getTradableChange(tradable, ASSET_LIST_TIMEFRAMES[0].value)?.changePercent;
	}

	public isIndex(tradable: Tradable): boolean {
		return tradable instanceof Index;
	}

	public getV4RedirectURL(): string {
		return config.V4AppBaseUrl + "/?o=modal-buy&stepId=AssetSelection";
	}

	protected getRSFeeText(): string {
		return this.realStocksFacade.getRSFeeFormatted();
	}
}
